import * as React from 'react';
import { Field, FieldProps, FieldConfig } from 'formik';
import { Form } from 'antd';
import { FormItemProps } from 'antd/lib/form/FormItem';
import { isEqual } from 'lodash';
import { isComponentsEqual } from 'utils/reactUtils';
import {
  getFormikFieldError,
  getFormikFieldTouched,
} from 'utils/formUtils';

type Props = {
  showValidateSuccess?: boolean;
  showInitialErrorAfterTouched?: boolean;
  children: React.ReactNode;
  showValidateError?: boolean;
  name?: string;
} & FormItemProps &
  Pick<FieldConfig, 'validate'>;

type CustomFormItemProps = Props & FieldProps;

function areEqual(
  prevProps: CustomFormItemProps,
  nextProps: CustomFormItemProps,
) {
  const {
    children: prevChildren,
    form: prevForm,
    field: prevField,
    ...restPrevProps
  } = prevProps;
  const {
    children: nextChildren,
    form: nextForm,
    field: nextField,
    ...restNextProps
  } = nextProps;

  return (
    isEqual(restPrevProps, restNextProps) &&
    isEqual(prevForm.errors, nextForm.errors) &&
    isEqual(prevForm.touched, nextForm.touched) &&
    isComponentsEqual(prevChildren, nextChildren)
  );
}

const CustomFormItem = React.memo(
  ({
    field: { name },
    form: { errors = {}, touched = {}, initialErrors = {} },
    showInitialErrorAfterTouched,
    showValidateSuccess,
    showValidateError = true,
    ...props
  }: CustomFormItemProps) => {
    const error = getFormikFieldError(errors, name);
    const initialError = getFormikFieldError(initialErrors, name);
    const isTouched = getFormikFieldTouched(touched, name);
    const hasError = error !== undefined && isTouched;
    const hasInitialError = initialError !== undefined;
    const isValid = !error && isTouched;
    const showHelp =
      hasError ||
      (hasInitialError && (!isTouched || showInitialErrorAfterTouched));
    const showSuccessFeedback = isValid && !!showValidateSuccess;

    return (
      <Form.Item
        validateStatus={
          !showValidateError
            ? undefined
            : hasError || (hasInitialError && !isTouched)
              ? 'error'
              : showSuccessFeedback
                ? 'success'
                : undefined
        }
        hasFeedback={showSuccessFeedback}
        help={
          showValidateError &&
          showHelp && (
            <>
              {hasError && <li>{error}</li>}
              {hasInitialError &&
                (!isTouched || showInitialErrorAfterTouched) && (
                  <li>{initialError}</li>
                )}
            </>
          )
        }
        {...props}
      >
        {props.children}
      </Form.Item>
    );
  },
  areEqual,
);

export default function FormItem(props: Props) {
  const { name, children, validate, ...formItemProps } = props;

  if (!name) {
    return <Form.Item {...formItemProps}>{children}</Form.Item>;
  }

  return (
    <Field
      validate={validate}
      component={CustomFormItem}
      {...props}
      name={name}
    />
  );
}
